/**
 * Filter controls, second pass.
 *
 * What the first pass got wrong, and this fixes:
 *  - Controls were h-12 with border-2 — phone sizing. A filter row is chrome,
 *    not the content; it should not be the tallest thing on the page.
 *  - Search needed Enter. Now it debounces.
 *  - There was no way to see what was filtered without reading every control,
 *    and no way to clear one thing without hunting for its dropdown. Active
 *    filters are now chips you can dismiss individually.
 *  - "Reset" sat at the end of the control row as a peer of the filters, so it
 *    looked like another filter. It belongs with the chips it clears.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { use_debounced_value } from '@/hooks/use-debounced-value';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Label above a control, so every field in the row shares a baseline. */
export function FilterSlot({
  label,
  html_for,
  children,
  className,
  grow,
}: {
  label: string;
  html_for?: string;
  children: React.ReactNode;
  className?: string;
  /** Let this control absorb the leftover width (search usually). */
  grow?: boolean;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', grow && 'flex-1 basis-48', className)}>
      <label
        className="text-micro font-bold uppercase tracking-wide text-muted-foreground"
        htmlFor={html_for}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export type ActiveChip = {
  /** Stable key — also the test id suffix. */
  key: string;
  /** What was filtered, e.g. "Status". */
  label: string;
  /** What it was filtered to, e.g. "Awaiting vendor". */
  value: string;
  on_clear: () => void;
  /** Chip belongs to a filter that lives in the advanced drawer. */
  advanced?: boolean;
};

/**
 * What a page may hand us per chip.
 *
 * Pages build the list as `status && { ... }`, which yields the filter's own
 * empty value — usually `''` — when it isn't set. Accepting every falsy shape
 * keeps that idiom readable instead of forcing a `Boolean(x) &&` wrapper or a
 * cast at each of a dozen call sites.
 */
export type ChipInput = ActiveChip | false | null | undefined | '';

/**
 * The bar: a wrapping row of controls, then the active-filter chips.
 *
 * `advanced` holds the filters most triage never touches. They collapse by
 * default — a bar with nine visible controls is a form, and an admin reads a
 * form rather than scanning it.
 */
export function FilterBar({
  children,
  advanced,
  chips,
  on_clear_all,
  className,
}: {
  children: React.ReactNode;
  advanced?: React.ReactNode;
  chips?: ChipInput[];
  on_clear_all?: () => void;
  className?: string;
}) {
  const active = (chips ?? []).filter((c): c is ActiveChip => Boolean(c));
  const [open, set_open] = useState(false);
  // Keep the drawer open while any advanced filter is set — collapsing it
  // would hide a filter that is actively shaping the result set.
  const advanced_active = active.filter((c) => c.advanced);
  const expanded = open || advanced_active.length > 0;

  return (
    <div className={cn('flex flex-col gap-2 border-b border-border px-3 py-2.5', className)}>
      <div className="flex flex-wrap items-end gap-2">
        {children}
        {advanced && (
          <button
            type="button"
            onClick={() => set_open((o) => !o)}
            aria-expanded={expanded}
            className={cn(
              'console-control inline-flex shrink-0 items-center gap-1.5 px-2.5 font-semibold',
              expanded ? 'border-primary/50 text-primary' : 'text-muted-foreground',
            )}
            data-testid="filters-advanced-toggle"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">More filters</span>
            {advanced_active.length > 0 && (
              <Badge tone="primary" variant="solid" size="sm">
                {advanced_active.length}
              </Badge>
            )}
          </button>
        )}
      </div>

      {advanced && expanded && (
        <div
          className="flex flex-wrap items-end gap-2 rounded-panel bg-muted/40 p-2.5 animate-slide-up-fade"
          data-testid="filters-advanced"
        >
          {advanced}
        </div>
      )}

      {active.length > 0 && <FilterChips chips={active} on_clear_all={on_clear_all} />}
    </div>
  );
}

/**
 * Active filters as removable chips.
 *
 * The row wraps rather than clipping — a hidden filter that is still narrowing
 * the results is the single most confusing thing a list can do.
 */
export function FilterChips({
  chips,
  on_clear_all,
}: {
  chips: ActiveChip[];
  on_clear_all?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="filter-chips">
      <span className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
        Filtered by
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded-pill bg-primary/10 py-0.5 pl-2 pr-0.5 text-data font-medium text-primary"
          data-testid={`chip-${chip.key}`}
        >
          <span className="text-primary/70">{chip.label}:</span>
          <span className="min-w-0 truncate font-semibold">{chip.value}</span>
          <button
            type="button"
            onClick={chip.on_clear}
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Clear ${chip.label} filter`}
            data-testid={`chip-clear-${chip.key}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {on_clear_all && chips.length > 1 && (
        <button
          type="button"
          onClick={on_clear_all}
          className="ml-0.5 rounded-control px-1.5 py-0.5 text-data font-semibold text-muted-foreground underline-offset-2 transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="chips-clear-all"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

/**
 * Debounced search field.
 *
 * Local state drives what you see; the debounced copy drives the URL. The
 * effect syncing back from `value` is what makes "Clear all" and a pasted link
 * actually empty the box — without it the input keeps stale text that no longer
 * matches the query it appears to describe.
 */
export function SearchInput({
  value,
  on_change,
  placeholder = 'Search…',
  label = 'Search',
  delay = 350,
  test_id = 'filter-search',
  className,
}: {
  value: string;
  on_change: (next: string) => void;
  placeholder?: string;
  label?: string;
  delay?: number;
  test_id?: string;
  className?: string;
}) {
  const id = useId();
  const [text, set_text] = useState(value);
  const debounced = use_debounced_value(text, delay);
  const last_pushed = useRef(value);

  useEffect(() => {
    if (debounced === last_pushed.current) return;
    last_pushed.current = debounced;
    on_change(debounced);
    // on_change is recreated per render by the URL-state setter; depending on
    // it would fire this on every render instead of on every settled keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  useEffect(() => {
    if (value !== last_pushed.current) {
      last_pushed.current = value;
      set_text(value);
    }
  }, [value]);

  return (
    <FilterSlot label={label} html_for={id} grow className={className}>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          id={id}
          type="search"
          className="console-field w-full pl-8 pr-8"
          placeholder={placeholder}
          value={text}
          onChange={(e) => set_text(e.target.value)}
          data-testid={test_id}
        />
        {text && (
          <button
            type="button"
            onClick={() => set_text('')}
            className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Clear search"
            data-testid={`${test_id}-clear`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </FilterSlot>
  );
}

/** A plain single-select filter — the shape most of the bar is made of. */
export function SelectFilter<T extends string>({
  label,
  value,
  on_change,
  options,
  all_label = 'All',
  test_id,
  width,
}: {
  label: string;
  value: T | '';
  on_change: (next: T | '') => void;
  options: readonly { value: T; label: string }[];
  all_label?: string;
  test_id?: string;
  width?: string;
}) {
  const id = useId();
  return (
    <FilterSlot label={label} html_for={id}>
      <select
        id={id}
        className="console-select"
        style={width ? { width } : undefined}
        value={value}
        onChange={(e) => on_change(e.target.value as T | '')}
        data-testid={test_id}
      >
        <option value="">{all_label}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FilterSlot>
  );
}

/**
 * Tri-state boolean filter. `''` means "don't care", which is a different
 * thing from `false` and has to stay distinguishable — "not a dummy payment"
 * and "any payment" are not the same query.
 */
export function BoolFilter({
  label,
  value,
  on_change,
  true_label = 'Yes',
  false_label = 'No',
  any_label = 'Any',
  test_id,
}: {
  label: string;
  value: '' | 'true' | 'false';
  on_change: (next: '' | 'true' | 'false') => void;
  true_label?: string;
  false_label?: string;
  any_label?: string;
  test_id?: string;
}) {
  const id = useId();
  return (
    <FilterSlot label={label} html_for={id}>
      <select
        id={id}
        className="console-select"
        value={value}
        onChange={(e) => on_change(e.target.value as '' | 'true' | 'false')}
        data-testid={test_id}
      >
        <option value="">{any_label}</option>
        <option value="true">{true_label}</option>
        <option value="false">{false_label}</option>
      </select>
    </FilterSlot>
  );
}

/** A bounded numeric input, for amount floors and ceilings. */
export function NumberFilter({
  label,
  value,
  on_change,
  placeholder,
  test_id,
  prefix,
}: {
  label: string;
  value: string;
  on_change: (next: string) => void;
  placeholder?: string;
  test_id?: string;
  /** Unit marker rendered inside the field, e.g. the rupee sign. */
  prefix?: string;
}) {
  const id = useId();
  const [text, set_text] = useState(value);
  const debounced = use_debounced_value(text, 400);
  const last_pushed = useRef(value);

  useEffect(() => {
    if (debounced === last_pushed.current) return;
    last_pushed.current = debounced;
    on_change(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  useEffect(() => {
    if (value !== last_pushed.current) {
      last_pushed.current = value;
      set_text(value);
    }
  }, [value]);

  return (
    <FilterSlot label={label} html_for={id}>
      <div className="relative">
        {prefix && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-data text-muted-foreground"
          >
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          className={cn('console-field w-28 tabular', prefix && 'pl-6')}
          placeholder={placeholder}
          value={text}
          onChange={(e) => set_text(e.target.value)}
          data-testid={test_id}
        />
      </div>
    </FilterSlot>
  );
}
